import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { CHART_NEUTRAL, CHART_ONLINE } from "../../../../styles/theme_constants";
import { comparisonLabel } from "../../../../utils/fleetPeriods";
import { change, DASH, formatNumber, formatPercent, onlineRate } from "../../../../utils/fleetDerivations";
import { axisLabel } from "./panelHelpers";
import type { PanelProps } from "./panelHelpers";
import { barSeries, baseChartOptions, CHART_HEIGHT } from "./dashboardChart";
import StatTile from "./StatTile";
import UnavailableNotice from "./UnavailableNotice";

/**
 * BOF-76 — how many charge points exist, and how many are online (FR-005 – FR-007).
 *
 * The workbook drew this as bars with the online rate as a line on a second axis. Two scales on one chart
 * invite reading a crossing point that means nothing, so the rate lives in a tile and the chart stacks
 * online and not-online charge points against a single count axis — their sum is the total.
 *
 * The census is a state, not an activity: the period reports its standing on its last recorded day, and
 * it is never scaled by the 30-day lens. Where no snapshot exists the panel says so rather than showing 0.
 */
const ChargePointsPanel: React.FC<PanelProps> = ({ selected, previous, trend }) => {
  const census = selected.census;
  const comparedTo = comparisonLabel(previous);

  const data = useMemo(
    () => ({
      labels: trend.map(axisLabel),
      datasets: [
        barSeries("Online", trend.map((p) => p.census?.online ?? null), CHART_ONLINE, false),
        barSeries(
          "Not online",
          trend.map((p) => (p.census ? p.census.total - p.census.online : null)),
          CHART_NEUTRAL,
          true,
        ),
      ],
    }),
    [trend],
  );

  return (
    <Card title="Charge points" className="h-full">
      {census ? (
        <>
          <div className="grid mb-2">
            <div className="col-12 sm:col-4">
              <StatTile
                label="Total charge points"
                value={formatNumber(census.total)}
                change={change(census.total, previous?.census?.total)}
                comparedTo={comparedTo}
              />
            </div>
            <div className="col-6 sm:col-4">
              <StatTile label="Online" value={formatNumber(census.online)} />
            </div>
            <div className="col-6 sm:col-4">
              <StatTile
                label="Online rate"
                value={formatPercent(onlineRate(census))}
                hint="Charge points online as a share of all charge points, on the last recorded day of the period."
              />
            </div>
          </div>
          <div className="text-xs text-color-secondary mb-2">
            Standing on {census.asOf}
            {census.source === "IMPORTED" && ", from the workbook's hand-kept monthly history"} · never scaled or summed
          </div>
        </>
      ) : (
        <div className="mb-2">
          <div className="text-2xl font-semibold text-900 mb-2">{DASH}</div>
          <UnavailableNotice reason={{ kind: "censusNotRecorded" }} inline />
        </div>
      )}
      <Chart type="bar" data={data} options={baseChartOptions({ stacked: true, yTitle: "Charge points" })} style={CHART_HEIGHT} />
    </Card>
  );
};

export default ChargePointsPanel;
